import { Link } from "react-router-dom";

import { Icon } from "@/lib/Icon";
import { track } from "@/lib/analytics";
import type { HomeTask } from "@/types/bootstrap";

// Map task `kind` to a destination. Backend can also send an explicit
// cta_href (used for view_delivered_order etc) which wins. Default kinds:
// book_appointment, book_consult, buy_consult, update_address,
// view_delivered_order.
function _hrefForTask(kind: string): string | null {
  if (kind === "book_appointment" || kind === "book_consult") {
    return "/app/clinic?tab=appointments&book=1";
  }
  if (kind === "book_peptide_consult") {
    // Evaluation-upsell visit — books the dedicated "Peptide Consult"
    // Healthie type so it reads distinctly on the clinician calendar.
    return "/app/clinic?tab=appointments&book=1&kind=peptide_consult";
  }
  if (kind === "buy_consult") {
    return "/app/marketplace";
  }
  if (kind === "update_address") {
    return "/app/account";
  }
  if (kind === "view_delivered_order") {
    return "/app/orders";
  }
  if (kind === "upload_id") {
    return "/app/clinic?tab=documents&upload=id";
  }
  if (kind === "update_payment") {
    // Backend normally sends an explicit cta_href carrying the dunning
    // link slug (which pays the open invoice outright); this is the
    // fallback when no cycle slug was available.
    return "/app/account?focus=payment";
  }
  return null;
}

/**
 * TaskList — image square + stacked title / description / CTA per card.
 * The whole card is the click target. Matches the new home design.
 */
export function TaskList({
  tasks,
  onTaskClick,
  emphasizeCta = false,
}: {
  tasks: HomeTask[];
  /** Fired when a task row is tapped — lets a host (e.g. the landing
   *  intake drawer) dismiss itself as the patient navigates in. */
  onTaskClick?: () => void;
  /** Render each CTA as a black bar with white blinking text (attention
   *  overlay). Default off = Home's sunken CTA row. */
  emphasizeCta?: boolean;
}) {
  if (tasks.length === 0) {
    return (
      <div className="font-body text-13 text-[#7C7C7C] p-15 border border-dashed border-[#ECECEC]">
        No open tasks — you&apos;re all caught up.
      </div>
    );
  }
  return (
    <ul className="flex flex-col">
      {tasks.map((t, i) => (
        <li key={t.id}>
          {/* Only the first row draws a top border; every later row drops it
              so adjacent cards share the upper row's bottom hairline instead
              of stacking two (there's no gap between them). */}
          <TaskRow
            task={t}
            first={i === 0}
            onTaskClick={onTaskClick}
            emphasizeCta={emphasizeCta}
          />
        </li>
      ))}
    </ul>
  );
}

// Placeholder art per task kind — PNGs live in /public/icons. The backend
// will eventually supply a per-task image_url that supersedes this map.
function taskImageForKind(kind: string): string | null {
  switch (kind) {
    case "upload_id":
      return "/icons/system_id_placeholder.png";
    case "finish_intake":
    case "complete_intake":
    case "treatment_intake":
    case "renewal_checkin":
      return "/icons/system_intake_placeholder.png";
    default:
      return null;
  }
}

/**
 * TaskImage — dark-gray square holding the task's illustration. Falls back
 * to a placeholder mark so the slot keeps its footprint when a kind has no
 * art yet.
 */
function TaskImage({
  src,
  alt,
  fullHeight = true,
}: {
  src?: string | null;
  alt: string;
  // Home rows force h-full so the image fills the row; the overlay card
  // stacks a full-width CTA below, so its image uses h-[unset] and sizes to
  // the text row instead.
  fullHeight?: boolean;
}) {
  return (
    <div
      className={`shrink-0 self-stretch w-[10rem] ${
        fullHeight ? "h-full" : "h-[unset]"
      } p-20 bg-[#333333] flex items-center justify-center overflow-hidden`}
    >
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-contain" />
      ) : (
        <div aria-hidden className="w-30 text-white/25">
          <Icon name="Mark Line" viewBox="0 0 32 30" />
        </div>
      )}
    </div>
  );
}

export function TaskRow({
  task,
  first = true,
  onTaskClick,
  emphasizeCta = false,
}: {
  task: HomeTask;
  // When stacked in a gapless list, only the first row keeps its top border;
  // later rows drop it so adjacent cards share a single hairline.
  first?: boolean;
  onTaskClick?: () => void;
  // Render the CTA row as a black bar with white blinking text (used in the
  // landing attention overlay to make the action pop). Home uses the default
  // sunken row.
  emphasizeCta?: boolean;
}) {
  const href = task.cta_href ?? _hrefForTask(task.kind);
  // Warning-toned tasks (today: failed payment) get the same wash +
  // left-rule treatment as the account-page billing banner, so the two
  // surfaces read as one alert rather than two unrelated designs.
  const warn = task.tone === "warning";
  const topBorder = first ? "" : " border-t-0";

  const titleBody = (
    <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5 leading-110">
      <div
        className="font-mono text-15 uppercase leading-110 text-balance"
        style={warn ? { color: "var(--warning)" } : { color: "black" }}
      >
        {task.title}
      </div>
      {task.body && (
        <p
          className={
            warn
              ? "font-body text-13 text-ink"
              : "font-body text-13 text-[#7C7C7C]"
          }
        >
          {task.body}
        </p>
      )}
    </div>
  );

  const cta = task.cta_label ? (
    <div
      className={
        emphasizeCta
          ? "flex items-center justify-between gap-10 h-44 px-15 bg-black font-body text-14 text-white"
          : href
            ? "flex items-center justify-between gap-10 h-40 px-15 border-t border-[#ECECEC] bg-black font-body text-14 text-white"
            : "flex items-center justify-between gap-10 h-40 px-15 border-t border-[#ECECEC] bg-[#D9D9D9] font-body text-14 text-ink-secondary"
      }
    >
      <span className="blink">{task.cta_label}</span>
      <span
        aria-hidden
        className="blink inline-flex items-center justify-center w-8"
      >
        <Icon name="Arrow" viewBox="0 0 10 12" />
      </span>
    </div>
  ) : null;

  // Overlay variant: image + text on a top row, then the CTA as a full-width
  // bar BELOW the whole card (spanning under the image too). Home variant:
  // image beside a text+CTA column, so the CTA is only as wide as the text.
  const inner = emphasizeCta ? (
    <div
      className={
        "flex flex-col border border-[#ECECEC] bg-white" +
        (first ? "" : " border-t-0")
      }
    >
      <div className="flex items-stretch">
        <TaskImage src={taskImageForKind(task.kind)} alt="" fullHeight={false} />
        {titleBody}
      </div>
      {cta}
    </div>
  ) : (
    <div
      className={
        (warn
          ? "flex items-stretch border border-l-4 bg-[var(--warning-wash)] border-[var(--warning)]"
          : "flex items-stretch border border-[#ECECEC] bg-white") + topBorder
      }
    >
      {/* Placeholder PNG per kind for now — the backend will supply a
          per-task image_url that supersedes this. */}
      <div>
        <TaskImage src={taskImageForKind(task.kind)} alt="" />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        {titleBody}
        {cta}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        onClick={() => {
          track("home_task_clicked", { kind: task.kind });
          onTaskClick?.();
        }}
        className="block hover:opacity-90 bg-raised"
      >
        {inner}
      </Link>
    );
  }
  return inner;
}
